import { Class } from '@/types';
import { dateFormatter } from '@utils/formatter';
import clsx from 'clsx';

import AddOneIcon from '@/icons/add-one.svg';
import AttentionIcon from '@/icons/attention.svg';
import CheckOneIcon from '@/icons/check-one.svg';
import FireIcon from '@/icons/fire.svg';
import LocationIcon from '@/icons/local-two.svg';
import PeoplePlusOneIcon from '@/icons/people-plus-one.svg';
import TvMinimalIcon from '@/icons/tv-minimal.svg';

type Status = 'full' | 'pending' | 'planned' | 'busy';

type CourseStatusProps = {
  status: Status;
  information: Class;
};

function CourseStatus(props: CourseStatusProps) {
  const { status, information } = props;

  if (status === 'busy') {
    return (
      <span className='class-information__status'>
        <FireIcon width={18} height={18} />
        Die Veranstaltung ist beliebt. Nur noch {information.free_places} Plätze frei.
      </span>
    );
  }

  if (status === 'full' && information.allow_online_waiting_list) {
    return (
      <span className='class-information__status'>
        <AttentionIcon width={18} height={18} />
        Leider ausgebucht. Die Warteliste ist geöffnet.
      </span>
    );
  }

  if (status === 'full') {
    return (
      <span className='class-information__status'>
        <AttentionIcon width={18} height={18} />
        Die Veranstaltung ist leider ausgebucht.
      </span>
    );
  }

  if (
    status === 'pending' &&
    information.max_participants - information.min_participants - information.free_places === 1
  ) {
    return (
      <span className='class-information__status'>
        <PeoplePlusOneIcon width={18} height={18} />
        Die nächste Anmeldung garantiert die Durchführung.
      </span>
    );
  }

  if (status === 'pending') {
    return (
      <span className='class-information__status'>
        <AddOneIcon width={18} height={18} />
        Es sind noch Plätze frei. Jetzt anmelden.
      </span>
    );
  }

  if (status === 'planned') {
    return (
      <span className='class-information__status'>
        <CheckOneIcon width={18} height={18} />
        Durchführung garantiert. Es sind noch Plätze frei.
      </span>
    );
  }

  return null;
}

type Props = { information: Class };

function ClassInformation(props: Props) {
  const { information } = props;

  let status: Status = 'planned';

  if (
    // current registrations < min_participants
    information.max_participants - information.free_places <
    information.min_participants
  ) {
    status = 'pending';
  }

  if (
    // current registrations >= min_participants
    information.max_participants - information.free_places >=
    information.min_participants
  ) {
    status = 'planned';
  }

  if (information.free_places && information.free_places <= 3) {
    status = 'busy';
  }

  if (information.free_places === 0) {
    status = 'full';
  }

  return (
    <article
      className={clsx('class-information', {
        'class-information--full': status === 'full',
        'class-information--pending': status === 'pending',
        'class-information--busy': status === 'busy',
        'class-information--planned': status === 'planned',
      })}
    >
      <div className='class-information__content'>
        <h3 className='class-information__title'>
          <time dateTime={information.class_startdate}>
            {dateFormatter.format(new Date(information.class_startdate))}
          </time>{' '}
          -{' '}
          <time dateTime={information.class_enddate}>{dateFormatter.format(new Date(information.class_enddate))}</time>
        </h3>
        <span className='class-information__location'>
          {information.course_presence_format_id === 'IN_PERSON' ? (
            <>
              <LocationIcon width={12} height={12} />
              {information.room_address}
            </>
          ) : (
            <>
              <TvMinimalIcon width={12} height={12} />
              Online
            </>
          )}
        </span>
      </div>
      <footer className='class-information__footer'>
        <CourseStatus status={status} information={information} />
      </footer>
    </article>
  );
}

export default ClassInformation;
